<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { Link, UserRoundPlus } from 'lucide-vue-next'

import { Invite } from '@/types'
import { generateDisplayName } from '@/utils/user'
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import MemberSearchInput from '@/components/member/MemberSearchInput.vue'
import MemberInviteList from '@/components/member/MemberInviteList.vue'
import MemberManualEntryForm from '@/components/member/MemberManualEntryForm.vue'
import type { SearchMember } from '@/components/member/MemberSearchInput.vue'
import type { InviteRowItem } from '@/components/member/MemberInviteList.vue'
import type { ManualEntryValues } from '@/components/member/MemberManualEntryForm.vue'

const PROJECT_ROLES = [
  { value: 'project_admin', label: 'Admin' },
  { value: 'project_editor', label: 'Editor' },
  { value: 'project_viewer', label: 'Viewer' },
]

const store = useStore()
const route = useRoute()

const currentMember = computed(() => store.getters['user/getMember'])
const projectId = route.params.project_id as string
const project = computed(() => store.getters['project/current'])
const orgId = computed(() => project.value?.organization_id)

const isOpen = ref(false)
const isInviteInProgress = ref(false)
const isLinkCopied = ref(false)
const invite = ref<Invite | null>(null)
const showManualEntry = ref(false)
const inviteRows = ref<InviteRowItem[]>([])
const manualRole = ref('project_editor')
const manualInitialContact = ref('')


const inviteLink = computed(() => (invite.value ? urlFromUuid(invite.value.uuid) : null))

const close = () => {
  isOpen.value = false
  invite.value = null
  inviteRows.value = []
  showManualEntry.value = false
  isInviteInProgress.value = false
  isLinkCopied.value = false
  manualInitialContact.value = ''
}

watch(isOpen, (newValue) => {
  if (!newValue) {
    invite.value = null
    inviteRows.value = []
    showManualEntry.value = false
    isInviteInProgress.value = false
    isLinkCopied.value = false
    manualInitialContact.value = ''
  }
})

const copyLink = () => {
  if (!inviteLink.value) return
  navigator.clipboard.writeText(inviteLink.value)
  isLinkCopied.value = true
  setTimeout(() => {
    isLinkCopied.value = false
  }, 1000)
}

const urlFromUuid = (uuid: string) => {
  return `${location.protocol}//${window.location.host}/invite/${uuid}`
}

const onSelectMember = (member: SearchMember) => {
  if (inviteRows.value.some(r => r.member_id === member.member_id)) return

  inviteRows.value.push({
    member_id: member.member_id,
    first_name: member.first_name || '',
    last_name: member.last_name || '',
    email: member.email || null,
    phone: member.phone || null,
    avatar_url: member.avatar_url || null,
    display_name: member.display_name || null,
    verified: member.verified ?? false,
    role: 'project_editor',
  })
}

const onAddNew = (contact = '') => {
  manualInitialContact.value = contact
  showManualEntry.value = true
}

const onManualSubmit = (values: ManualEntryValues, role: string) => {
  inviteRows.value.push({
    member_id: null,
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    phone: values.phone,
    avatar_url: null,
    display_name: generateDisplayName(values.first_name, values.last_name),
    role,
  })

  showManualEntry.value = false
  manualRole.value = 'project_editor'
  manualInitialContact.value = ''
}

const onManualCancel = () => {
  showManualEntry.value = false
  manualInitialContact.value = ''
}

const removeRow = (index: number) => {
  inviteRows.value.splice(index, 1)
}

const updateRole = (index: number, role: string) => {
  inviteRows.value[index].role = role
}

const onSubmit = async () => {
  if (inviteRows.value.length === 0) return

  isInviteInProgress.value = true
  try {
    let lastInvite: Invite | null = null
    const failedRows: InviteRowItem[] = []
    for (const row of inviteRows.value) {
      const payload: any = {
        inviter_id: currentMember.value.member_id,
        project_id: projectId,
        role_name: row.role,
        email: row.email,
        phone: row.phone,
        first_name: row.first_name,
        last_name: row.last_name,
        fullname: row.display_name || generateDisplayName(row.first_name || '', row.last_name || ''),
      }
      const result = await store.dispatch('project/addInvite', payload)
      if (result) {
        lastInvite = result
      } else {
        failedRows.push(row)
      }
    }
    if (failedRows.length > 0) {
      inviteRows.value = failedRows
    } else if (inviteRows.value.length === 1 && lastInvite) {
      invite.value = lastInvite
    } else {
      close()
    }
  } finally {
    isInviteInProgress.value = false
  }
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger as-child>
      <slot>
        <Button variant="ghost" class="h-8 px-2 flex justify-start" data-testid="add-member-button">
          <UserRoundPlus class="w-5 h-5 text-muted-foreground" />
        </Button>
      </slot>
    </DialogTrigger>

    <DialogContent class="max-w-[450px]">
      <div data-testid="invite-member-dialog">
        <DialogHeader v-if="!invite">
          <DialogTitle class="mb-0.5">Invite a New Member</DialogTitle>
        </DialogHeader>

      <div v-if="invite" class="text-center pt-3" data-testid="invite-success-section">
        <div class="mb-5">
          <h3 class="font-bold text-lg">Invite Link Created</h3>
          <p class="text-sm">Copy and share the link with the person.</p>

          <div v-if="invite.email && invite.email_status === 'sent'" class="mt-3">
            <p class="text-sm">
              A link will also be sent to
              <span class="font-semibold">{{ invite.email }}</span>
            </p>
          </div>
        </div>
        <div v-if="inviteLink">
          <Input :model-value="inviteLink" readonly @click="copyLink" class="cursor-pointer" data-testid="invite-link-input" />
          <div class="mt-2 flex justify-center space-x-2">
            <Button variant="secondary" class="flex items-center" @click="copyLink" data-testid="copy-link-button">
              <Link class="w-3 h-3 mr-1" />
              <span v-if="!isLinkCopied">Copy link</span>
              <span v-else>Link copied!</span>
            </Button>
            <Button variant="ghost" @click="close" data-testid="invite-close-button">
              Close
            </Button>
          </div>
        </div>
      </div>

      <div v-else class="space-y-4 mt-2">
        <MemberSearchInput
          v-if="orgId"
          :org-id="orgId"
          :project-id="projectId"
          @select="onSelectMember"
          @add-new="onAddNew"
        />

        <MemberManualEntryForm
          v-if="showManualEntry"
          v-model:role="manualRole"
          :roles="PROJECT_ROLES"
          default-role="project_editor"
          mode="emailOrPhone"
          :initial-contact="manualInitialContact"
          @submit="onManualSubmit"
          @cancel="onManualCancel"
        />

        <div class="max-h-[300px] overflow-y-auto">
          <MemberInviteList
            :rows="inviteRows"
            :roles="PROJECT_ROLES"
            @remove="removeRow"
            @update:role="updateRole"
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" @click="close" data-testid="invite-close-button">Close</Button>
          <Button @click="onSubmit" :disabled="inviteRows.length === 0 || isInviteInProgress" data-testid="invite-create-button">
            <Spinner v-if="isInviteInProgress" class="w-4 h-4" />
            <span v-else>{{ inviteRows.length > 1 ? `Invite (${inviteRows.length})` : 'Invite' }}</span>
          </Button>
        </DialogFooter>
      </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
